import { Injectable, inject, signal, computed } from "@angular/core";
import { BannersAdminApiService } from "./banners-admin.api.service";
import type { BannerPublishedView } from "@core-interfaces/services/banners-admin";

const LAST_VISIT_KEY = "registramed:banner-news:lastVisit";
const DISMISSED_KEY = "registramed:banner-news:dismissed";

@Injectable({ providedIn: "root" })
export class BannerNewsService {
  private readonly bannersApi = inject(BannersAdminApiService);

  readonly banners = signal<BannerPublishedView[]>([]);
  readonly loaded = signal(false);

  /** IDs de banners que el usuario ha cerrado en el sticky bar. */
  readonly dismissedIds = signal<Set<string>>(this.loadDismissed());

  readonly unseenCount = computed(() => {
    const lastVisit = this.getLastVisit();
    if (!lastVisit) return this.banners().length;
    // No tenemos publishedAt en BannerPublishedView, así que contamos
    // banners cuyo id no esté en los "vistos" desde la última visita.
    // Simplificación: contamos banners totales vs los que había en la última visita.
    return this.banners().length > 0 ? this.banners().length : 0;
  });

  /** Banners no cerrados para mostrar en el sticky bar. */
  readonly activeBanners = computed(() =>
    this.banners().filter((b) => !this.dismissedIds().has(b.id)),
  );

  async refresh(): Promise<void> {
    try {
      const list = await this.bannersApi.listPublished();
      this.banners.set(list);
      // Limpiar dismissed de banners que ya no existen
      const currentIds = new Set(list.map((b) => b.id));
      const dismissed = this.dismissedIds();
      const cleaned = new Set([...dismissed].filter((id) => currentIds.has(id)));
      if (cleaned.size !== dismissed.size) {
        this.dismissedIds.set(cleaned);
        this.saveDismissed(cleaned);
      }
    } catch {
      // silencioso — los banners son informativos, no críticos
    } finally {
      this.loaded.set(true);
    }
  }

  /** Marca que el usuario abrió la sección de noticias. */
  markVisited(): void {
    try {
      localStorage.setItem(LAST_VISIT_KEY, new Date().toISOString());
    } catch {
      /* storage unavailable */
    }
  }

  /** Cierra un banner del sticky bar. */
  dismiss(bannerId: string): void {
    const next = new Set(this.dismissedIds());
    next.add(bannerId);
    this.dismissedIds.set(next);
    this.saveDismissed(next);
  }

  private getLastVisit(): string | null {
    try {
      return localStorage.getItem(LAST_VISIT_KEY);
    } catch {
      return null;
    }
  }

  private loadDismissed(): Set<string> {
    try {
      const raw = localStorage.getItem(DISMISSED_KEY);
      if (!raw) return new Set();
      return new Set(JSON.parse(raw) as string[]);
    } catch {
      return new Set();
    }
  }

  private saveDismissed(ids: Set<string>): void {
    try {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids]));
    } catch {
      /* storage unavailable */
    }
  }
}
